<template>
  <div>
    <v-container fluid>
      <v-skeleton-loader
        :loading="!flagNav"
        transition="scale-transition"
        max-height="64"
        type="table-heading"
      >
        <v-toolbar :image="SubNavbarBackGround" color="primary" class="header-title">
          <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ $t("_SetupUserGroup") }}</v-toolbar-title>
          <v-spacer />
          <div class="d-flex pr-4">
            <v-toolbar-title class="title" :style="'color: #' + FontColorTitle">{{ today }}</v-toolbar-title>
          </div>
        </v-toolbar>
      </v-skeleton-loader>
      
      <v-card class="setup-form-card mt-6" elevation="2">
        <v-card-title class="form-header">
          <div class="header-content">
            <v-icon size="28" color="white" class="header-icon">mdi-cog-outline</v-icon>
            <div class="header-text">
              <h3 class="form-title">{{ $t("_SetupUserGroup") }}</h3>
              <p class="form-subtitle">Configure group settings and model access</p>
            </div>
          </div>
          <v-btn 
            color="success" 
            variant="elevated" 
            class="save-button"
            @click="Save"
            :loading="dialog"
          >
            <v-icon start size="20">mdi-check-circle-outline</v-icon>
            {{ $t("save") }}
          </v-btn>
        </v-card-title>

        <v-divider class="form-divider"></v-divider>

        <v-card-text class="form-content">
          <!-- Basic Information Section -->
          <div class="form-section">
            <div class="section-header">
              <v-icon size="20" color="primary" class="section-icon">mdi-information-outline</v-icon>
              <h4 class="section-title">Basic Information</h4>
            </div>
            
            <v-row class="form-row">
              <v-col cols="12" md="6">
                <div class="input-wrapper">
                  <label class="input-label">
                    {{ $t('groupname') }}
                    <span class="required-indicator">*</span>
                  </label>
                  <v-text-field
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    v-model="groupname"
                    placeholder="Enter group name"
                    class="modern-input"
                    :rules="[v => !!v || 'Group name is required']"
                  >
                    <template v-slot:append-inner>
                      <v-tooltip location="top" max-width="300px">
                        <template v-slot:activator="{ props }">
                          <v-icon
                            size="16"
                            v-bind="props"
                            color="grey-darken-1"
                            class="help-icon"
                          >
                            mdi-help-circle-outline
                          </v-icon>
                        </template>
                        <span>Enter a descriptive name for this user group</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </div>
              </v-col>
              
              <v-col cols="12" md="6">
                <div class="input-wrapper">
                  <label class="input-label">
                    {{ $t('groupObjectID') }}
                  </label>
                  <v-text-field
                    variant="outlined"
                    density="comfortable"
                    hide-details="auto"
                    v-model="groupObjectID"
                    placeholder="Enter object ID (optional)"
                    class="modern-input"
                  >
                    <template v-slot:append-inner>
                      <v-tooltip location="top" max-width="300px">
                        <template v-slot:activator="{ props }">
                          <v-icon
                            size="16"
                            v-bind="props"
                            color="grey-darken-1"
                            class="help-icon"
                          >
                            mdi-help-circle-outline
                          </v-icon>
                        </template>
                        <span>Azure AD Object ID for integration (optional)</span>
                      </v-tooltip>
                    </template>
                  </v-text-field>
                </div>
              </v-col>
            </v-row>
          </div>

          <v-divider class="section-divider"></v-divider>

          <!-- Model Configuration Section -->
          <div class="form-section">
            <div class="section-header">
              <v-icon size="20" color="primary" class="section-icon">mdi-robot-outline</v-icon>
              <h4 class="section-title">Model Configuration</h4>
            </div>
            
            <v-row class="form-row">
              <v-col cols="12" md="6">
                <div class="input-wrapper">
                  <label class="input-label">
                    Model Grouping
                    <span class="required-indicator">*</span>
                  </label>
                  <v-autocomplete
                    v-model:search="searchGroupModel"
                    :items="listDDGroupModel || []"
                    item-title="name"
                    item-value="id"
                    clearable
                    variant="outlined"
                    hide-details="auto"
                    v-model="groupModelID"
                    density="comfortable"
                    placeholder="Select model grouping"
                    class="modern-input"
                    :rules="[v => !!v || 'Model grouping is required']"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon size="20" color="grey-darken-1">mdi-layers-outline</v-icon>
                    </template>
                    <template v-slot:append-inner>
                      <v-tooltip location="top" max-width="300px">
                        <template v-slot:activator="{ props }">
                          <v-icon
                            size="16"
                            v-bind="props"
                            color="grey-darken-1"
                            class="help-icon"
                          >
                            mdi-help-circle-outline
                          </v-icon>
                        </template>
                        <span>Select the model grouping category for this user group</span>
                      </v-tooltip>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>
              
              <v-col cols="12" md="6">
                <div class="input-wrapper">
                  <label class="input-label">
                    Available Models
                    <span class="required-indicator">*</span>
                  </label>
                  <v-autocomplete
                    :items="listDDModel || []"
                    chips
                    closable-chips
                    item-title="name"
                    item-value="modelDisplayID"
                    multiple
                    hide-details="auto"
                    variant="outlined"
                    clearable
                    v-model="modelDisplay"
                    density="comfortable"
                    placeholder="Select models"
                    class="modern-input"
                    :rules="[v => (v && v.length > 0) || 'At least one model is required']"
                  >
                    <template v-slot:prepend-inner>
                      <v-icon size="20" color="grey-darken-1">mdi-brain</v-icon>
                    </template>
                    <template v-slot:chip="{ props, item }">
                      <v-chip
                        v-bind="props"
                        :text="item.raw.name"
                        color="primary"
                        variant="tonal"
                        size="small"
                        closable
                        class="model-chip"
                      >
                        <template v-slot:prepend>
                          <v-icon size="14">mdi-robot</v-icon>
                        </template>
                      </v-chip>
                    </template>
                    <template v-slot:append-inner>
                      <v-tooltip location="top" max-width="300px">
                        <template v-slot:activator="{ props }">
                          <v-icon
                            size="16"
                            v-bind="props"
                            color="grey-darken-1"
                            class="help-icon"
                          >
                            mdi-help-circle-outline
                          </v-icon>
                        </template>
                        <span>Select which AI models this group can access</span>
                      </v-tooltip>
                    </template>
                  </v-autocomplete>
                </div>
              </v-col>
            </v-row>
          </div>
        </v-card-text>
      </v-card>

      <v-card class="card2" style="margin-top: 1rem">
        <v-container fluid>
          <v-data-table-server
            class="modern-table"
            v-model="userSelected"
            :items="userList || []"
            :items-per-page="options.itemsPerPage"
            :items-length="searchTotalTable"
            item-key="userId"
            v-model:options="options"
            :headers="headers"
            show-select
            :loading="loadingTable"
            @update:model-value="onSelectionChange"
          >
            <template v-slot:top>
            
              <v-card variant="outlined" class="pa-2 search-panel">
                <v-row style="display: flex; align-items: center;" no-gutters>
                  <v-col cols="3" class="pa-2">
                    <v-text-field
                      id="username-field-search-usergroupdetail"
                      :label="$t('username')"
                      density="comfortable"
                      variant="outlined"
                      prepend-inner-icon="mdi-text-box-search-outline"
                      hide-details
                      clearable
                      v-model="username"
                      @keydown.enter="Search"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="3" class="pa-2">
                    <v-text-field
                      id="email-field-search-usergroupdetail"
                      :label="$t('email')"
                      v-model="email"
                      density="comfortable"
                      variant="outlined"
                      prepend-inner-icon="mdi-email-outline"
                      hide-details
                      clearable
                      @keydown.enter="Search"
                    ></v-text-field>
                  </v-col>
                  <v-col cols="1" class="pa-2 d-flex align-center justify-end">
                    <v-btn 
                      icon="mdi-magnify"
                      color="primary" 
                      @click="Search"
                      size="50"
                    >
                    </v-btn>
                  </v-col>
                  <v-col cols="3" offset="2" class="d-flex justify-end align-center">
                    <v-btn color="primary" style="border-radius: 10px;" variant="elevated" @click="Add">
                      <v-icon start size="20">mdi-plus-circle-outline</v-icon>
                      {{ $t("Add") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </v-card>
            </template>
            <!-- เพิ่ม custom header slot เพื่อควบคุม select all checkbox -->
            <template v-slot:header.data-table-select="{ props, on }">
              <v-checkbox-btn
                v-bind="props"
                v-on="on"
                :indeterminate="userSelected.length > 0 && userSelected.length < userList.length"
                :model-value="userSelected.length === userList.length && userList.length > 0"
                @update:model-value="toggleSelectAll"
              />
            </template>
            
            <!-- เพิ่ม custom item slot เพื่อควบคุม individual checkboxes -->
            <template v-slot:item.data-table-select="{ item, index }">
              <v-checkbox-btn
                :model-value="isSelected(item)"
                @update:model-value="toggleSelect(item, $event)"
              />
            </template>
          </v-data-table-server>
        </v-container>
      </v-card>
    </v-container>

    <v-dialog v-model="dialog" persistent width="300">
      <v-card color="#1976D2">
        <v-card-text class="text-white">
          Loading Please Wait...
          <v-progress-linear
            indeterminate
            color="#FAA91B"
            class="mb-0"
          ></v-progress-linear>
        </v-card-text>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CryptoJS from "crypto-js";
import Cookies from "js-cookie";
import LogTrace from "@/api/function.js";
import store from "../store";

export default {
  name: "SetupUserGroup",
  inject: ["flagNav","FontColorTitle","SubNavbarBackGround"],
  mixins: [LogTrace],
  data: () => ({
    isInitialized: false,
    show1: false,
    formHasErrors: false,
    permission: {
      add: true,
      edit: true,
      delete: true,
    },
    mailcheck: false,
    phonecheck: false,
    filters: {
      username: [],
      email: [],
      employeeName: [],
      userType: [],
    },
    DDRole: [
      {
        name: "Admin",
        value: 1,
      },
      {
        name: "ผู้ใช้งานทั่วไป",
        value: 2,
      },
    ],
    numberRules: [(v) => !!v || "Input is required!"],
    rules: {
      required: (value) => !!value || "Required.",
      min: (v) => v.length >= 10 || "Min 10 numbers",
    },
    Email: null,
    gg: null,
    UserType: null,
    dialog: false,
    edit: {
      id: null,
      username: null,
      firstname: null,
      lastname: null,
      email: null,
      phone: null,
      password: null,
      roleId: null,
      userType: null,
      model: [],
    },
    user: {
      id: null,
      username: null,
      firstname: null,
      lastname: null,
      email: null,
      phone: null,
      password: null,
      roleId: 2,
      userType: null,
      model: [],
    },
    listDDModel: [],
    oldPassword: "",
    Editdialog: false,
    Adddialog: false,
    isSearch: false,
    username: "",
    email: "",
    totalTable: 0,
    itemsPerPage: 10,
    options: {},
    today: null,
    userListDDL: [],
    userList: [],
    headers: [],
    secretKey: "01234567890123456789012345678901",
    roleId: Cookies.get("roleId"),
    isFirst: true,
    searchTotalTable: 0,
    sortBy: [],
    sortDesc: [],
    fileImportUser: null,
    flagTemplate: false,
    groupID: null,
    listDDGroupModel: [],
    DDQuickModel: null,
    searchGroupModel: null,
    groupname: null,
    groupObjectID: null,
    groupModelID: null,
    modelDisplay: null,
    userSelected: [],
    loadingTable: false,
  }),
  
  watch: {
    options: {
      handler() {
        if (!this.isInitialized) return;
        this.Get();
      },
      deep: true,
    },
    searchGroupModel(val) {
      this.GetGroupModel(val);
    },
  },
  
  beforeUnmount() {
    store.state.groupSecurityID = null;
  },
  
  mounted() {
    this.dialog = true;
    this.groupID = this.$route.query.id;
    store.state.groupSecurityID = this.$route.query.id;
    
    if (this.groupID != null && this.groupID != undefined && this.groupID != "") {
      this.headers = [
        { title: this.$t("no"), align: "center", sortable: false, key: "number" },
        { title: this.$t("username"), align: "start", sortable: true, key: "username" },
        { title: this.$t("email"), align: "start", sortable: true, key: "email" },
        { title: "Name", align: "start", sortable: true, key: "fullName" },
      ];
      
      Promise.all([
        this.GetDate(),
        this.GetGroupModel(),
        this.GetModel(),
        this.GetSetupByID(),
      ]).then(() => {
        this.dialog = false;
        this.Get();
        this.isInitialized = true;
      });
    } else {
      this.$router.push({
        name: "GroupUserManagement",
      });
    }
  },
  
  computed: {
    filteredUsers() {
      return this.userList.filter((d) => {
        return Object.keys(this.filters).every((f) => {
          return this.filters[f].length < 1 || this.filters[f].includes(d[f]);
        });
      });
    },
    form() {
      return {
        username: this.user.username,
        firstname: this.user.firstname,
        lastname: this.user.lastname,
        email: this.user.email,
        phone: this.user.phone,
        password: this.user.password,
      };
    },
    headersTemplate() {
      return [
        { text: "CSV", align: "center" },
        { text: "Excel", align: "center" },
      ];
    },
  },

  methods: {
    onSelectionChange(selected) {
      this.userSelected = selected;
    },
    
    isSelected(item) {
      return this.userSelected.some(selected => selected.userId === item.userId);
    },
    
    toggleSelect(item, checked) {
      if (checked) {
        if (!this.isSelected(item)) {
          this.userSelected.push(item);
        }
      } else {
        this.userSelected = this.userSelected.filter(selected => selected.userId !== item.userId);
      }
    },
    
    toggleSelectAll(checked) {
      if (checked) {
        this.userSelected = [...this.userList];
      } else {
        this.userSelected = [];
      }
    },
    GetGroupModel(searchText = '') {
      let self = this;
      self.$call.GetDDGroupModel(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            self.listDDGroupModel = dataResponse.data || [];
          } else {
            self.listDDGroupModel = [];
          }
        },
        (errorResource) => {
          console.error(errorResource);
          self.listDDGroupModel = [];
        }
      );
    },

    PhoneNumberValidation(phno) {
      let phoneno = /^\d{10}$/;
      return phoneno.test(phno) || "Invalid Phone format";
    },

    validateEmail(email) {
      // eslint-disable-next-line no-useless-escape
      let re = /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email) || "invalid email";
    },
    
    Search() {
      let self = this;
      self.isSearch = true;
      self.options.page = 1;
      self.Get();
    },
    
    GetDate() {
      let today1 = new Date();
      let dd = today1.getDate();
      let mm = today1.getMonth() + 1; //January is 0!

      let yyyy = today1.getFullYear();
      if (dd < 10) {
        dd = "0" + dd;
      }
      if (mm < 10) {
        mm = "0" + mm;
      }

      this.today = dd + "/" + mm + "/" + yyyy;
    },

    GetModel() {
      let self = this;
      self.$call.DDGPTModel(
        (dataResponse) => {
          if (dataResponse.status == 0) {
            self.listDDModel = dataResponse.data || [];
          } else {
            self.listDDModel = [];
          }
        },
        (errorResource) => {
          console.error(errorResource);
          self.listDDModel = [];
        }
      );
    },
    
    Get() {
      try {
        let self = this;
        self.dialog = true;
        self.loadingTable = true;
        
        let { page, itemsPerPage, sortBy } = self.options;
        let sortField = null;
        let sortOrder = null;

        if (sortBy && sortBy.length > 0) {
          sortField = sortBy[0].key;
          sortOrder = sortBy[0].order === 'desc';
        }
        
        let temp = {
          page: self.isSearch ? 1 : page,
          perPage: itemsPerPage != -1 ? itemsPerPage : self.totalTable,
          username: self.username,
          eMail: self.email,
          sortDesc: sortOrder,
          sortBy: sortField,
          groupID: self.groupID,
        };
        
        self.$call.GetTableSetupUserGroupManagement(
          temp,
          (dataResponse) => {
            if (dataResponse.status == 0) {
              self.searchTotalTable = dataResponse.data.total;
              self.userList = dataResponse.data.data || [];
              self.userListDDL = dataResponse.data.data || [];
              self.totalTable = self.isFirst ? dataResponse.data.total : self.totalTable;
            } else {
              self.userList = [];
              self.totalTable = 0;
              self.searchTotalTable = 0;
            }
            self.dialog = false;
            self.loadingTable = false;
            self.isSearch = false;
            self.isFirst = false;
          },
          (errorResource) => {
            self.dialog = false;
            self.loadingTable = false;
            self.isSearch = false;
            self.userList = [];
            self.totalTable = 0;
            self.searchTotalTable = 0;
            console.error(errorResource);
          }
        );
      } catch (error) {
        this.LogTrace(error, "Get");
      }
    },
    
    GetSetupByID() {
      let self = this;
      self.$call.GetSetupByID(self.groupID, (dataResponse) => {
        if (dataResponse && dataResponse.data) {
          self.groupname = dataResponse.data.groupName;
          self.groupObjectID = dataResponse.data.groupObject_ID;
          self.groupModelID = dataResponse.data.groupModelID;
          self.modelDisplay = dataResponse.data.modelDisplayID;
        }
      });
    },
    
    Add() {
      let self = this;
      self.dialog = true;
      if (self.userSelected != null && self.userSelected.length > 0) {
        let ID = self.userSelected.map((i) => {
          return i.userId;
        });
        let data = {
          ID,
          GroupID: self.groupID,
        };
        self.$call.AddUserIntoGroup(
          data,
          (dataResponse) => {
            self.dialog = false;
            if (dataResponse.status == 0) {
              self.Get();
              self.$swal.fire({
                icon: "success",
                title: dataResponse.message,
                showConfirmButton: false,
                timer: 1500,
              });
              this.userSelected = [];
            } else {
              self.$swal.fire({
                icon: "warning",
                title: dataResponse.data,
                showConfirmButton: false,
                timer: 1500,
              });
            }
          },
          (err) => {
            self.dialog = false;
            self.$swal.fire({
              icon: "error",
              title: err,
              showConfirmButton: false,
              timer: 1500,
            });
            console.log(err);
          }
        );
      } else {
        self.$swal.fire({
          icon: "warning",
          title: "Please select a user to add to the group.",
          showConfirmButton: false,
          timer: 1500,
        });
        self.dialog = false;
      }
    },
    
    Save() {
      let self = this;
      if (
        self.groupname != null &&
        self.groupname != "" &&
        ((self.groupModelID != null && self.groupModelID != "") ||
          (self.modelDisplay != null && self.modelDisplay != ""))
      ) {
        self.dialog = true;
        let groupID = self.groupID;
        let temp = {
          id: groupID,
          GroupName: self.groupname,
          GroupObject_ID: self.groupObjectID,
          GroupModelID: self.groupModelID,
          ModelDisplayID:
            self.modelDisplay != null ? JSON.stringify(self.modelDisplay) : null,
        };
        self.$call.UpdateUserGroup(
          temp,
          (dataResponse) => {
            self.dialog = false;
            if (dataResponse.data == "Successfully" && self.groupObjectID != null) {
              self.$call.SyncDataEntraIDGroup(
                self.groupID,
                (response) => {
                  self.$swal.fire({
                    icon: "success",
                    title: dataResponse.message,
                    showConfirmButton: false,
                    timer: 1500,
                  });
                  self.GetSetupByID();
                },
                (error) => {
                  self.dialog = false;
                  self.$swal.fire({
                    icon: "error",
                    title: error,
                    showConfirmButton: false,
                    timer: 1500,
                  });
                  console.log(error);
                }
              );
            } else if (dataResponse.data == "Successfully") {
              self.$swal.fire({
                icon: "success",
                title: dataResponse.message,
                showConfirmButton: false,
                timer: 1500,
              });
            } else {
              self.$swal.fire({
                icon: "warning",
                title: dataResponse.data,
                showConfirmButton: false,
                timer: 1500,
              });
            }
          },
          (err) => {
            self.dialog = false;
            self.$swal.fire({
              icon: "error",
              title: err,
              showConfirmButton: false,
              timer: 1500,
            });
            console.log(err);
          }
        );
      } else {
        if (
          self.groupname == null ||
          (self.groupname == "" &&
            (self.groupModelID == null ||
              self.groupModelID == "" ||
              self.modelDisplay == null ||
              self.modelDisplay == ""))
        ) {
          self.$swal.fire({
            icon: "warning",
            title: "Please fill the required fields!!!",
            showConfirmButton: false,
            timer: 1500,
          });
        } else if (
          self.groupModelID == null ||
          self.groupModelID == "" ||
          self.modelDisplay == null ||
          self.modelDisplay == ""
        ) {
          self.$swal.fire({
            icon: "warning",
            title: "Please select your model!!!",
            showConfirmButton: false,
            timer: 1500,
          });
        }
      }
    },
    
    Sync() {
      let self = this;
      self.dialog = true;
      self.$call.SyncDataEntraIDGroup(
        self.groupID,
        (dataResponse) => {
          if (dataResponse.status == 0) {
            self.$swal
              .fire({
                icon: "success",
                title: dataResponse.message,
                showConfirmButton: false,
                timer: 1500,
              })
              .then(() => {
                self.Get();
              });
          } else {
            self.$swal.fire({
              icon: "warning",
              title: dataResponse.data,
              confirmButtonColor: "#3085d6",
              confirmButtonText: "Close",
            });
          }
          self.dialog = false;
        },
        (err) => {
          console.log(err);
          self.dialog = false;
        }
      );
    },
    
    decryptData(encryptedText, key) {
      const decryptedBytes = CryptoJS.AES.decrypt(encryptedText, key);
      const decryptedText = decryptedBytes.toString(CryptoJS.enc.Utf8);
      return decryptedText;
    },
    
    decryptShiftData(encryptedText, shift) {
      let chars = encryptedText.split("");
      let decryptedChars = [];

      for (let i = 0; i < chars.length; i++) {
        let c = chars[i];

        if (/[a-zA-Z]/.test(c)) {
          let offset = c === c.toUpperCase() ? "A".charCodeAt(0) : "a".charCodeAt(0);
          c = String.fromCharCode(
            ((c.charCodeAt(0) - offset - shift + 26) % 26) + offset
          );
        } else if (/[0-9]/.test(c)) {
          c = String.fromCharCode(
            ((c.charCodeAt(0) - "0".charCodeAt(0) - shift + 10) % 10) + "0".charCodeAt(0)
          );
        }

        decryptedChars.push(c);
      }

      return decryptedChars.join("");
    },
  },
};
</script>

<style scoped>
/* Base Card Styles */
.card2 {
  border-radius: 10px !important;
  margin-bottom: 2rem;
  padding-top: 1rem;
}

.title {
  color: white;
}

/* Data Table Styles */
:deep(.v-data-table tbody tr) {
  transition: all 0.2s ease;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
}

:deep(.v-data-table tbody tr:hover) {
  background: rgba(0, 0, 0, 0.02);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

:deep(.v-data-table-header) {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 2px solid #dee2e6;
}

:deep(.v-data-table-header th) {
  color: #495057 !important;
  font-weight: 700 !important;
  font-size: 0.875rem !important;
  padding: 1rem !important;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 2px solid rgba(25, 118, 210, 0.1) !important;
}

/* Search Panel */
.search-panel {
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.search-panel:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  border-color: rgba(25, 118, 210, 0.2);
}

/* Setup Form Card */
.setup-form-card {
  border-radius: 16px !important;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.setup-form-card:hover {
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12) !important;
  transform: translateY(-2px);
}

/* Form Header */
.form-header {
  background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
  color: white !important;
  padding: 24px !important;
  display: flex !important;
  justify-content: space-between !important;
  align-items: center !important;
  min-height: 80px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
}

.header-text {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.form-title {
  font-size: 1.5rem;
  font-weight: 600;
  margin: 0;
  color: white;
  line-height: 1.2;
}

.form-subtitle {
  font-size: 0.875rem;
  margin: 0;
  opacity: 0.9;
  color: white;
  font-weight: 400;
}

.save-button {
  height: 44px !important;
  min-width: 120px;
  font-weight: 600 !important;
  border-radius: 12px !important;
  text-transform: none !important;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3) !important;
  background: linear-gradient(135deg, #4caf50 0%, #388e3c 100%) !important;
}

.save-button:hover {
  box-shadow: 0 6px 16px rgba(76, 175, 80, 0.4) !important;
  transform: translateY(-1px);
}

.save-button:disabled {
  opacity: 0.7 !important;
  transform: none !important;
}

/* Form Content */
.form-content {
  padding: 32px !important;
  background: white;
}

.form-section {
  margin-bottom: 32px;
  animation: slideInUp 0.5s ease-out;
}

.form-section:last-child {
  margin-bottom: 0;
}

.section-header {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid rgba(25, 118, 210, 0.1);
}

.section-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #1976d2;
  letter-spacing: 0.025em;
}

.section-divider {
  margin: 24px 0 !important;
  opacity: 0.3;
}

/* Input Styling */
.input-wrapper {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.input-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #424242;
  line-height: 1.2;
  display: flex;
  align-items: center;
  gap: 4px;
}

.required-indicator {
  color: #d32f2f;
  font-weight: 700;
}

.modern-input :deep(.v-field) {
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.modern-input :deep(.v-field:hover) {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  border-color: rgba(25, 118, 210, 0.3);
}

.modern-input :deep(.v-field--focused) {
  box-shadow: 0 4px 16px rgba(25, 118, 210, 0.2);
  border-color: #1976d2;
}

.modern-input :deep(.v-field__outline),
.modern-input :deep(.v-field--focused .v-field__outline) {
  --v-field-border-opacity: 0;
}

.modern-input :deep(.v-field__input) {
  padding: 14px 16px;
  font-size: 0.9rem;
  color: #424242;
}

.modern-input :deep(.v-field__input::placeholder) {
  color: #9e9e9e;
  opacity: 1;
}

.modern-input :deep(.v-field__prepend-inner) {
  padding-right: 8px;
}

.modern-input :deep(.v-field__append-inner) {
  padding-left: 8px;
}

.help-icon {
  cursor: help;
  transition: all 0.2s ease;
  opacity: 0.7;
}

.help-icon:hover {
  opacity: 1;
  color: #1976d2 !important;
  transform: scale(1.1);
}

.model-chip {
  margin: 2px !important;
  border-radius: 8px !important;
  font-weight: 500 !important;
}

/* Animations */
@keyframes slideInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Design */
@media (max-width: 768px) {
  .form-header {
    flex-direction: column;
    gap: 16px;
    text-align: center;
    padding: 20px !important;
  }
  
  .header-content {
    flex-direction: column;
    text-align: center;
  }
  
  .save-button {
    width: 100%;
  }
  
  .form-content {
    padding: 20px !important;
  }
  
  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
}

@media (max-width: 480px) {
  .form-title {
    font-size: 1.25rem;
  }
  
  .form-subtitle {
    font-size: 0.8rem;
  }
  
  .section-title {
    font-size: 1rem;
  }
  
  .input-label {
    font-size: 0.8rem;
  }
}

/* Dark Theme Support */
.v-theme--dark .setup-form-card {
  background: linear-gradient(135deg, #1e1e1e 0%, #2d2d2d 100%);
  border-color: rgba(255, 255, 255, 0.12);
}

.v-theme--dark .form-content {
  background: #1e1e1e;
}

.v-theme--dark .input-label {
  color: #e0e0e0;
}

.v-theme--dark .section-title {
  color: #64b5f6;
}

.v-theme--dark .modern-input :deep(.v-field) {
  background: #2d2d2d;
  border-color: rgba(255, 255, 255, 0.12);
}

.v-theme--dark .modern-input :deep(.v-field:hover) {
  border-color: rgba(100, 181, 246, 0.3);
}

.modern-table :deep(.v-table__wrapper) {
  max-height: 600px;
  overflow-x: auto;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(25, 118, 210, 0.3) rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper thead) {
  position: sticky;
  top: 0;
  z-index: 2;
}

.modern-table :deep(.v-table__wrapper thead th) {
  background: rgb(var(--v-theme-surface, 255, 255, 255));
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar) {
  width: 6px;
  height: 8px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-track) {
  background: rgba(0, 0, 0, 0.05);
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb) {
  background: rgba(25, 118, 210, 0.3);
  border-radius: 3px;
}

.modern-table :deep(.v-table__wrapper::-webkit-scrollbar-thumb:hover) {
  background: rgba(25, 118, 210, 0.5);
}
</style>